import {Box, Button, Chip, Divider, Stack, Typography, useTheme} from "@mui/material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import {useEffect, useState} from "react";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {useMutation, useQuery} from "@tanstack/react-query";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import {ArrowBack, Delete} from "@mui/icons-material";
import {Link, useNavigate, useParams} from "react-router-dom";
import {CustomCellRendererProps} from "ag-grid-react";
import {urls} from "@routes";
import {useDialogs} from "@toolpad/core";
import {useSnackbar} from "notistack";
import { format } from "date-fns";
import { Add } from "@mui/icons-material";
import { WorkspaceDetail, SelectWorkspacePopup } from "@admin/AdminUserDetails";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

interface PaginationData {
	user_email: string
	data: Campaign[]
	total_count: number
	total_pages: number
	current_page: number
	page_size: number
	workspace_details: WorkspaceDetail[]
}

interface Campaign {
	uid: string
	workspace: string
	created_on_ts: number
	name: string
	custom_emails_per_day: number
	status: string
	scheduled_start_datetime_ts: number | null
	reply_to_address: string | null
	sending_domains: Array<string>
	bounces: number
	total_replies: number
	total_sent: number
	campaign_type: string
	total_reply: number
	reply_rate: number
}


export default function AdminUserCampaigns() {
	const {userId} = useParams();
	const { user } = useAuth();
	const {enqueueSnackbar} = useSnackbar();
	const navigate = useNavigate();
	const theme = useTheme();
	const dialog = useDialogs();
	
	const [pageData, setPageData] = useState<PaginationData>();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	const [workspace, setWorkspaces] = useState<WorkspaceDetail | null>(null);
	const [openAddUpdatePost, setOpenAddUpdatePost] = useState(false)
	const [exportLoading, setExportLoading] = useState(false);

	// Query to fetch all campaigns.
	const adminUserCampaignsQuery = useQuery({
		queryKey: [
			"adminUserCampaignsQuery",
			userId,
			page,
			pageSize,
			search
		],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/get-user-campaigns/?user_id=${userId}&page=${page}&page_size=${pageSize}&search=${search}`
			),
		gcTime: 0,
		refetchOnWindowFocus: false,
		retry: retryFn,
	});

	useEffect(() => {
		if (adminUserCampaignsQuery.data?.data) {
			const response = adminUserCampaignsQuery.data.data;

			setPageData({
				user_email: response.user_email,
				data: response.data || [],
				total_count: response.total_count || 0,
				total_pages: response.total_pages || 0,
				current_page: response.current_page || 1,
				page_size: response.page_size || 50,
				workspace_details: response.workspace_details,
			});
		}
	}, [adminUserCampaignsQuery.data]);

	// Mutation to cancel campaign.
	const cancelCampaignMutation = useMutation({
		mutationKey: ["adminCancelCampaign"],
		mutationFn: (uid: string) => authenticateAndPostData("/admin/cancel-campaign/", {
			"uid": uid,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			adminUserCampaignsQuery.refetch();
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	})

	const CampaignDetailsLink = (cellProps: CustomCellRendererProps) => {
		return (
			<Link
				to={urls["adminUserCampaignDetails"].replace(":userId", userId || "").replace(":campaignUID", cellProps.data["uid"])}
				style={{color: theme.palette.text.primary}}>
				{cellProps.value}
			</Link>
		)
	}

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	function timestampFormatter(params: ValueFormatterParams) {
		if (params.value !== null) {
			return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
		} else {
			return "---"
		}
	}

	function sendingDomainsFormatter(params: ValueFormatterParams) {
		return params.value.join(", ");
	}

	function campaignStatusChipColor(status: string) {
		switch (status) {
			case "creating":
				return "warning";

			case "created":
				return "default";

			case "scheduled":
				return "primary";

			case "running":
				return "primary";

			case "complete":
				return "success";

			case "cancelled":
				return "error";

			default:
				return "default";
		}
	}

	const StatusBadge = (params: CustomCellRendererProps) => {
		return (
			<Chip size={"small"} color={campaignStatusChipColor(params.value)} label={params.value}/>
		)
	}

	const CancelCampaignButton = (params: CustomCellRendererProps) => {
		return (
			<IconButton size={"small"}
									color={"error"}
									disabled={!["created", "running", "paused"].includes(params.data["status"])}
									onClick={async () => {
										const cancelConfirmed = await dialog.confirm(
											`Are you sure you want to cancel campaign ${params.data["name"]}?`,
											{
												title: "Cancel Campaign",
												cancelText: "No",
												okText: "Yes",
											}
										);
										if (cancelConfirmed) {
											cancelCampaignMutation.mutate(params.data["uid"]);
										}
									}}>
				<Delete/>
			</IconButton>
		)
	}

	const campaignColumns: CSVColumn<any>[] = [
    { header: "Campaign UID", accessor: "uid" },
    { header: "Campaign Name", accessor: "name" },
    { header: "Workspace ID", accessor: "workspace_id" },
    { header: "Workspace Name", accessor: "workspace_name" },
    { header: "Status", accessor: "status" },
    { header: "Campaign Type", accessor: "campaign_type" },
    { header: "Emails / Day", accessor: "custom_emails_per_day" },
    { header: "Total Sent", accessor: "total_sent" },
    { header: "Total Replies", accessor: "total_replies" },
    { header: "Bounces", accessor: "bounces" },
    { header: "Reply-To Address", accessor: (row) => row.reply_to_address || "N/A" },
    { 
        header: "Sending Domains", 
        accessor: (row) => (Array.isArray(row.sending_domains) ? row.sending_domains.join("; ") : "") 
    },
    { 
        header: "Scheduled Start Date", 
        accessor: (row) => (row.scheduled_start_datetime_ts ? new Date(row.scheduled_start_datetime_ts).toLocaleString() : "N/A") 
    },
    { 
        header: "Created On", 
        accessor: (row) => (row.created_on_ts ? new Date(row.created_on_ts).toLocaleString() : "N/A") 
    },
];

	const ExportUserCampaignsCsvButton = () => {		

		const handleExportCampaigns = async () => {
			setExportLoading(true);

			try {
				const response = await authenticateAndFetchData(
					`/admin/get-user-campaigns/?user_id=${userId}&export_all=true`
				);
				
				const campaignsToExport = response?.data?.data || [];

				if (!campaignsToExport.length) {
					enqueueSnackbar("No campaign data available to export.", { variant: "error" });
					return;
				}

				const success = exportToCSV(
					campaignsToExport,
					campaignColumns,					
					getExportFilename(user?.email ?? "unknown", "UserCampaigns")
				);

				if (success) {
					enqueueSnackbar("Campaigns exported successfully!", { variant: "success" });
				} else {
					enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
				}
			} catch (err) {
				console.error("Export error:", err);
				enqueueSnackbar("Error while exporting campaigns.", { variant: "error" });
			} finally {
				setExportLoading(false);
			}
		};

		return (
			<Button
				startIcon={<Downloading />}
				variant="contained"
				color="primary"
				onClick={handleExportCampaigns}
				disabled={exportLoading}
			>
				{exportLoading ? "Exporting..." : "Export"}
			</Button>
		);
	};

	const CreateCampaignActions = () => (
			<Stack direction={"row"} spacing={2}>							
				<Button variant={"contained"}
								size={"small"}
								startIcon={<Add/>}								
								onClick={() => {
									setOpenAddUpdatePost(true)
								}}>
					Create Campaign
				</Button>
				<ExportUserCampaignsCsvButton />
			</Stack>
		)

	const columnCustomizerLabel = {
		name: "Campaign Name",
		uid: "Campaign UID",
		created_on_ts: "Created On",
		workspace_name: "Workspace",
		workspace_id: "Workspace Name",
		scheduled_start_datetime_ts: "Scheduled Start",
		total_sent: "Total Emails Sent",
		total_replies: "Total Replies",
		reply_to_address: "Reply To",
		sending_domains: "Subdomains",
		bounces: "Bounced",
		status: "Status",				
	};

	// Columns for campaigns table.
	const columnDefs: ColDef[] = [
		{field: "name", headerName: "Campaign Name", cellRenderer: CampaignDetailsLink, flex: 2},
		{field: "uid", headerName: "Campaign UID"},
		{
			field: "created_on_ts",
			headerName: "Created On",
			valueFormatter: timestampFormatter,
			comparator: (valueA, valueB, nodeA, nodeB) => {
				return nodeA.data["created_on_ts"] > nodeB.data["created_on_ts"] ? 1 : -1;
			},
			flex: 2,
		},
		{field: "workspace_name", headerName: "Workspace Name"},
		{ field: "workspace_id", headerName: "Workspace ID" },
		// {field: "custom_emails_per_day", headerName: "Custom Email Limit"},
		{
			field: "scheduled_start_datetime_ts",
			headerName: "Scheduled Start",
			valueFormatter: timestampFormatter,
			comparator: (valueA, valueB, nodeA, nodeB) => {
				let value1 = nodeA.data["scheduled_start_datetime_ts"] !== null ? nodeA.data["scheduled_start_datetime_ts"] : 0;
				let value2 = nodeB.data["scheduled_start_datetime_ts"] !== null ? nodeB.data["scheduled_start_datetime_ts"] : 0;

				if (value1 === value2) return 0;
				return value1 > value2 ? 1 : -1;
			},
		},
		{field: "reply_rate", headerName: "Reply Rate"},
		{field: "total_sent", headerName: "Total Emails Sent"},
		{field: "total_replies", headerName: "Total Replies"},
		{field: "reply_to_address", headerName: "Reply To"},
		{field: "sending_domains", headerName: "Subdomains", valueFormatter: sendingDomainsFormatter},
		{field: "bounces", headerName: "Bounced"},    
		{
			field: "campaign_type",
			headerName: "Campaign Type",
			valueFormatter: ({ value }) =>
				value === null || value === undefined || value === "" ? "---" : value,
		},
		{field: "status", headerName: "Status", cellRenderer: StatusBadge},
		{headerName: "Cancel", cellRenderer: CancelCampaignButton, resizable: false},
	]

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (!pageData && adminUserCampaignsQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (adminUserCampaignsQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={adminUserCampaignsQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={2} sx={{pb: 4, height: "100%"}}>
				<Stack direction={"row"} spacing={2} alignItems={"center"}>
					<IconButton onClick={() => navigate(urls["adminUserDetails"].replace(":userId", userId || ""))}><ArrowBack/></IconButton>
					<Typography fontWeight={"bold"} variant={"h5"}>Campaigns ({pageData.user_email})</Typography>
				</Stack>
				<Divider/>
				<Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
					<PaginatedServerSideDataTable
						columns={columnDefs}
						paginationData={pageData}
						loading={adminUserCampaignsQuery.isFetching}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						onSearchChange={handleSearchChange}
						searchPlaceholder="Search campaign name, uid, status..."
						columnLabels={columnCustomizerLabel} actions={CreateCampaignActions()}
					/>
				</Box>
				<SelectWorkspacePopup open={openAddUpdatePost}
										userId={userId!}
										workspaceDetails={pageData.workspace_details}
										workspace={workspace}
										setWorkspaces={setWorkspaces}
										onClose={() => setOpenAddUpdatePost(false)}
										onSuccess={() => {
											setOpenAddUpdatePost(false);
										}}
										url={urls["adminCreateCampaigns"]}
										/>
			</Stack>
		)
	} else {
		// Ideally it should not reach here.
		return <></>
	}
}
